import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Form, Table } from 'react-bootstrap';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useCurrencyFormatter } from '../../../../../hooks/useCurrencyFormatter';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import {
  applyMeritPercent,
  CompensationReviewSort,
  CompensationReviewSortColumn,
  defaultItemsSort,
  meritFromAmounts,
  sortItemsGroupedByEmployee,
  toCorporate,
} from '../logic';
import { CompensationReviewItem } from '../types';
import { PayBandCell } from './PayBandCell';

type Props = {
  items: CompensationReviewItem[];
  // The review's corporate currency — the target for the bracketed conversion.
  currency: string;
  // When false the grid is fully read-only (approver view / submitted+ status).
  editable: boolean;
  // The Include column is hidden once a review has been submitted.
  showIncludeColumn: boolean;
  onNewAmountChange: (itemId: string, newAmount: number) => void;
  onIncludedChange: (itemId: string, included: boolean) => void;
};

const frequencyKey = (item: CompensationReviewItem): string =>
  `frequencies.${item.frequency.toLowerCase()}`;

type SortableHeaderProps = {
  column: CompensationReviewSortColumn;
  label: string;
  className?: string;
  sort: CompensationReviewSort;
  onSort: (column: CompensationReviewSortColumn) => void;
  ariaSortLabel: string;
};

// A clickable column header. Clicking sorts by the column ascending; clicking
// the active column again flips the direction.
const SortableHeader: React.FC<SortableHeaderProps> = ({
  column,
  label,
  className,
  sort,
  onSort,
  ariaSortLabel,
}) => {
  const isActive = sort.column === column;
  return (
    <th
      className={className}
      aria-sort={
        isActive
          ? sort.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }>
      <span
        role="button"
        tabIndex={0}
        aria-label={ariaSortLabel}
        className="compensation-sortable"
        onClick={() => onSort(column)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSort(column);
          }
        }}>
        {label}
        {isActive && (
          <FlairIcon
            icon={
              sort.direction === 'asc'
                ? 'caret-up-outline'
                : 'caret-down-outline'
            }
            size="xs"
          />
        )}
      </span>
    </th>
  );
};

// `(≈ …)` approximation in the corporate currency, e.g. `(≈ 5.000,00 €)`.
const ConvertedHint: React.FC<{
  amount: number;
  item: CompensationReviewItem;
  corporateCurrency: string;
}> = ({ amount, item, corporateCurrency }) => {
  const formatCurrency = useCurrencyFormatter();
  // Skip when already in the corporate currency, or when there is no rate to
  // convert with (don't present an un-converted amount as a corporate one).
  if (item.currency === corporateCurrency || item.rateToCorporate === null) {
    return null;
  }
  return (
    <span className="d-block text-muted small compensation-converted-hint">
      {`(≈ ${formatCurrency({
        value: toCorporate(amount, item.rateToCorporate),
        currency: corporateCurrency,
      })})`}
    </span>
  );
};

export const CompensationReviewItemsTable: React.FC<Props> = ({
  items,
  currency,
  editable,
  showIncludeColumn,
  onNewAmountChange,
  onIncludedChange,
}) => {
  const t = useNamespacedTranslation('compensationReviews.table');

  const [sort, setSort] = useState<CompensationReviewSort>(defaultItemsSort);

  // Toggle direction when re-clicking the active column, otherwise start a new
  // column ascending.
  const handleSort = (column: CompensationReviewSortColumn) =>
    setSort((current) =>
      current.column === column
        ? {
            column,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
          }
        : { column, direction: 'asc' },
    );

  // Sort keeps each employee's rows grouped together (an employee may have more
  // than one salary line in a review).
  const sortedItems = useMemo(
    () => sortItemsGroupedByEmployee(items, sort),
    [items, sort],
  );

  const sortableHeaderProps = (
    column: CompensationReviewSortColumn,
    label: string,
    className?: string,
  ): SortableHeaderProps => ({
    column,
    label,
    className,
    sort,
    onSort: handleSort,
    ariaSortLabel: t('sortBy', { column: label }),
  });

  return (
    <Table responsive hover className="card-table table-nowrap align-middle">
      <thead>
        <tr>
          <SortableHeader {...sortableHeaderProps('employee', t('employee'))} />
          <SortableHeader {...sortableHeaderProps('position', t('position'))} />
          <SortableHeader
            {...sortableHeaderProps('frequency', t('frequency'))}
          />
          <SortableHeader
            {...sortableHeaderProps('reference', t('reference'), 'text-end')}
          />
          <SortableHeader
            {...sortableHeaderProps(
              'meritPercent',
              t('meritPercent'),
              'text-end',
            )}
          />
          <SortableHeader
            {...sortableHeaderProps(
              'newAmount',
              t('newAmount'),
              'text-end compensation-new-amount',
            )}
          />
          <th>{t('payBand')}</th>
          {showIncludeColumn && <th className="text-center">{t('include')}</th>}
        </tr>
      </thead>
      <tbody>
        {sortedItems.map((item) => {
          const meritPercent = meritFromAmounts(
            item.referenceAmount,
            item.newAmount,
          );
          // Excluded rows are dropped on submit, so present them as greyed-out
          // and read-only — only the Include toggle stays interactive.
          const isExcluded = !item.included;
          const fieldsEditable = editable && !isExcluded;

          return (
            <tr
              key={item.id}
              className={classNames({
                'compensation-item-excluded': isExcluded,
              })}>
              <td className="fw-bold">{item.employee.name}</td>
              <td>{item.position}</td>
              <td>{t(frequencyKey(item))}</td>
              <td className="text-end">
                <FormattedCurrency
                  value={item.referenceAmount}
                  currency={item.currency}
                />
                <ConvertedHint
                  amount={item.referenceAmount}
                  item={item}
                  corporateCurrency={currency}
                />
              </td>
              <td className="text-end">
                {fieldsEditable ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    className="compensation-merit-input ms-auto text-end"
                    value={meritPercent}
                    aria-label={t('meritPercent')}
                    onChange={(event) => {
                      // Ignore empty / non-numeric input (e.g. mid-edit when the
                      // field is cleared) so we never persist a spurious 0.
                      const raw = event.target.value;
                      if (raw === '') {
                        return;
                      }
                      const parsed = Number(raw);
                      if (Number.isNaN(parsed)) {
                        return;
                      }
                      onNewAmountChange(
                        item.id,
                        applyMeritPercent(item.referenceAmount, parsed),
                      );
                    }}
                  />
                ) : (
                  `${meritPercent.toFixed(2)}%`
                )}
              </td>
              <td className="text-end compensation-new-amount">
                {fieldsEditable ? (
                  <>
                    <Form.Control
                      type="number"
                      step="0.01"
                      className="compensation-amount-input ms-auto text-end fw-bold"
                      value={item.newAmount}
                      aria-label={t('newAmount')}
                      onChange={(event) => {
                        // Ignore empty / non-numeric input (e.g. mid-edit when
                        // the field is cleared) so we never persist a spurious 0.
                        const raw = event.target.value;
                        if (raw === '') {
                          return;
                        }
                        const parsed = Number(raw);
                        if (Number.isNaN(parsed)) {
                          return;
                        }
                        onNewAmountChange(item.id, parsed);
                      }}
                    />
                    <ConvertedHint
                      amount={item.newAmount}
                      item={item}
                      corporateCurrency={currency}
                    />
                  </>
                ) : (
                  <span className="fw-bold">
                    <FormattedCurrency
                      value={item.newAmount}
                      currency={item.currency}
                    />
                    <ConvertedHint
                      amount={item.newAmount}
                      item={item}
                      corporateCurrency={currency}
                    />
                  </span>
                )}
              </td>
              <td>
                <PayBandCell
                  payBand={item.payBand}
                  amount={item.newAmount}
                  currency={item.currency}
                />
              </td>
              {showIncludeColumn && (
                <td className="text-center compensation-include-cell">
                  <Form.Check
                    type="switch"
                    checked={item.included}
                    disabled={!editable}
                    aria-label={t('include')}
                    onChange={(event) =>
                      onIncludedChange(item.id, event.target.checked)
                    }
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
